import React from 'react';
import { call, delay, fork, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { push, replace } from 'redux-first-history';
import { stringify } from 'query-string';
import { Contact, ContactInfo, ContactReshardStatus, ExportStatus, ProductType, UnsubReason } from '@dev/ee-api/ee-api-models';
import { find, forEach } from 'lodash-es';

import i18n from '@/../i18n';
import dayjs from '@/services/tools/dayjs';
import getSafeRuleListName from '@/services/tools/getSafeRuleListName/getSafeRuleListName';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { HideToast, ShowToast } from '@/store/toast/actions';
import { ShowDialog } from '@/store/dialog/actions';
import { store } from '@shared-store/shared-store';
import {
    CancelReshardStatusPolling,
    ContactGetGrowthError,
    ContactGetGrowthSuccess,
    ContactGetHistoryError,
    ContactGetHistorySuccess,
    ContactsBulkUpdateError,
    ContactsBulkUpdateSuccess,
    ContactsDeleteError,
    ContactsDeleteSuccess,
    ContactsLoadingError,
    ContactsLoadingSuccess,
    ContactsOverviewLoadingError,
    ContactsOverviewLoadingSuccess,
    ContactsStartLoading,
    ContactUpdateError,
    ContactUpdateSuccess,
    GetContactsPatternError,
    GetContactsPatternSuccess,
    GetContactsUnsubscribedError,
    GetContactsUnsubscribedOverviewError,
    GetContactsUnsubscribedOverviewSuccess,
    GetContactsUnsubscribedSuccess,
    PostContactsError,
    PostContactsSuccess,
    PostContactsWithPatternError,
    PostContactsWithPatternSuccess,
    SetPostParams,
    SingleContactLoadingError,
    SingleContactLoadingSuccess,
} from '@/store/contacts/actions';
import {
    CheckReshardContactsStatusAction,
    ContactsActionTypes,
    ContactsFetchStartAction,
    ContactsUnsubscribedPaginationInterface,
    ExportContactsUnsubscribedAction,
    GetContactsUnsubscribedAction,
    GetContactsUnsubscribedOverviewAction,
    SingleContactFetchStartAction,
    ContactsGetGrowthStartAction,
} from '@/store/contacts/types';
import { SetAnalyticsOpen } from '@/store/upload/actions';
import {
    // addContactToListPost,
    bulkUpdateContacts,
    checkReshardStatus,
    checkTaskStatus,
    deleteContacts,
    editSingleContact,
    exportContactsUnsubscribed,
    getContactHistory,
    getContacts,
    getContactsGrowth,
    getContactsPattern,
    getContactsUnsubscribed,
    getContactsUnsubscribedOverview,
    loadContactsOverview,
    loadSingleContact,
    postContacts,
    postContactsPattern,
    reshardContacts,
} from '@/services/ElasticApi';
import { ApplicationState } from '@/store';
import { calculateActivePage } from '@/services/tools/handleUrlParams';
import { DEFAULT_PER_PAGE_COUNT, SHORT_DATE_FORMAT } from '@/services/constants';
import { extractUnsubscribedContactsStatusCounts } from '@/services/tools/extractUnsubscribedContactsStatusCount';
import { ExportsPaths, BillingPaths, pathsParams } from '@/services/dictionaries/Paths';
import { TotalContactHeaders } from '@/services/dictionaries/Headers';
import { ThunkSuccess } from '@/store/thunk/actions';
import { ThunkSuccessAction } from '@/store/thunk/types';
import { getAccountStore } from '@/store/account/actions';
import { ContactLimitReachModal } from '@/components/Contacts/Upload/ContactLimitReachModal';
import { isReseller } from '@/services/Secuirty';
import { sendEventToGTM } from '@dev/ee-tools';
import { Logger } from '@/services/errors/appLogger';
import { errorToast } from './commonGenerators/errorToast';

const _ALL_CONTACTS_LIST = '0'; //number sent to backend as listname when we are adding to All Contacts

const getLoadedContacts = (state: ApplicationState): ContactInfo[] => state.contacts?.collection || [];

function* getContactsOverview(action): any {
    const { from, to } = action.payload;
    try {
        const contactsOverview = yield call(loadContactsOverview, { From: from, To: to });
        yield put(ContactsOverviewLoadingSuccess(contactsOverview));
    } catch (e) {
        yield put(ContactsOverviewLoadingError());
        Logger.error(e);
    }
}

function* getGrowth(action: ContactsGetGrowthStartAction): any {
    const { from, to, isUrlUpdateDisabled } = action.payload;
    try {
        const newContacts = yield call(getContactsGrowth, from, to);
        const urlQueryParams = {
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            ...(to ? { to: dayjs(to).format(SHORT_DATE_FORMAT) } : {}),
        };
        yield put(ContactGetGrowthSuccess(newContacts.data));
        if (isUrlUpdateDisabled) {
            return;
        }
        yield put(replace({ search: stringify(urlQueryParams as any) }));
    } catch (e) {
        yield put(ContactGetGrowthError());
        Logger.error(e);
    }
}

function* watchGetContactGrowth(): any {
    yield takeLatest(ContactsActionTypes.GET_GROWTH_START, getGrowth);
}

function* watchGetContactsOverview(): any {
    yield takeLatest(ContactsActionTypes.FETCH_OVERVIEW_START, getContactsOverview);
}

function* getcontactsPattern(action): any {
    try {
        const contactsPattern = yield call(getContactsPattern, {
            fileName: action.payload.fileName,
            encodingName: action.payload.encodingName,
        });

        yield put(GetContactsPatternSuccess(contactsPattern));
    } catch (e) {
        yield put(GetContactsPatternError());
        Logger.error(e);
        yield put(ShowDialog(action.payload.errorModal));
    }
}

function* watchGetContactsPattern(): any {
    yield takeLatest(ContactsActionTypes.FETCH_PATTERN_START, getcontactsPattern);
}

function* postContactsWithPattern(action): any {
    const { meta } = action;
    //const { isStandalone }: ConfigState = yield select(getConfigStore);
    const { sourceType, ...payload } = action.payload;
    try {
        const contactsPattern = yield call(postContactsPattern, { ...payload });
        yield put(PostContactsWithPatternSuccess(contactsPattern, null, meta));
        sendEventToGTM('BulkContactUpload', { sourceType, newContactsCount: contactsPattern?.NewContacts });
    } catch (e) {
        const { GeneralInfo } = yield select(getAccountStore);
        yield put(PostContactsWithPatternError(meta));
        if (e.response.data.MessageID === 'upload_over_limit') {
            const selectPlanScreen = (productType = ProductType.Marketing.toLowerCase()) =>
                BillingPaths.BillingPlanList.replace(pathsParams.productCode, productType.toLowerCase());
            const upgradeLink =
                isReseller() && GeneralInfo?.AccountPaymentUrl
                    ? GeneralInfo.AccountPaymentUrl
                    : selectPlanScreen(GeneralInfo.ProductType);
            yield put(
                ShowDialog({
                    title: i18n.t('contactlimitreached'),
                    body: <ContactLimitReachModal data={e.response.data.MessageParts} />,
                    cancelLabel: i18n.t('close'),
                    confirmLabel: i18n.t('upgradeplan'),
                    onClickConfirm: (): void => {
                        window.location.replace(upgradeLink);
                    },
                    onClickCancel: (): void => {
                        store.dispatch(push(ContactsPaths.ContactsOverview));
                    },
                })
            );
        } else {
            yield put(
                ShowDialog({
                    title: i18n.t('error'),
                    body: e?.response?.data?.Error,
                    cancelLabel: i18n.t('close'),
                })
            );

            yield put(push(ContactsPaths.ContactsOverview));
        }
    }
}

function* watchPostContactsWithPattern(): any {
    yield takeLatest(ContactsActionTypes.CREATE_WITH_PATTERN_START, postContactsWithPattern);
}

function* addContacts(action): any {
    const { meta } = action;
    try {
        const contactsPattern = yield call(postContacts, { ...action.payload });
        yield put(PostContactsSuccess(contactsPattern, meta));
        sendEventToGTM('SingleContactUpload', { newContactsCount: contactsPattern?.NewContacts });
        if (action.type === ContactsActionTypes.CREATE_SINGLE) {
            yield put(
                ShowToast({
                    severity: 'info',
                    message: i18n.t('contactadded'),
                    duration: 6000,
                    onClose: HideToast,
                })
            );
            if (action.payload?.listName !== _ALL_CONTACTS_LIST) {
                //load contacts for selected list
                yield put(
                    ContactsStartLoading({
                        rule: `List = '${getSafeRuleListName(action.payload.listName)}'`,
                        limit: DEFAULT_PER_PAGE_COUNT,
                        offset: 0,
                    })
                );
            }
        }
        if (action.redirectPath) {
            yield put(push(action.redirectPath));
        }
    } catch (e) {
        yield put(PostContactsError(meta));
        if (action.type === ContactsActionTypes.CREATE_MULTIPLE) {
            yield put(SetAnalyticsOpen(false));
            yield put(push(ContactsPaths.ContactsOverview));
        }
        Logger.error(e);
        yield put(
            ShowDialog({
                title: i18n.t('error'),
                body: e?.response?.data?.Error,
                cancelLabel: i18n.t('close'),
            })
        );
    }
}

//create action
function* updatePostContactParams(action): any {
    try {
        //put data to store after delay
        yield delay(1000);
        yield put(SetPostParams(action.payload.postContactsParams));
    } catch (e) {
        console.log(e);
    }
}
//create watcher
function* watchUpdatePostContactParams(): any {
    yield takeLatest(ContactsActionTypes.SET_POST_START, updatePostContactParams);
}

function* watchPostContacts(): any {
    yield takeLatest(ContactsActionTypes.CREATE_START, addContacts);
}
function* watchAddSingleContact(): any {
    yield takeLatest(ContactsActionTypes.CREATE_SINGLE, addContacts);
}
function* watchAddMultipleContacts(): any {
    yield takeLatest(ContactsActionTypes.CREATE_MULTIPLE, addContacts);
}

const getCountsFromHeaders = (headers: Record<TotalContactHeaders, number>) => {
    const totalCount = Number(headers[TotalContactHeaders.TotalCount]) || 0;
    const bouncedCount = Number(headers[TotalContactHeaders.BouncedCount]) || 0;
    const unsubscribedCount = Number(headers[TotalContactHeaders.UnsubscribedCount]) || 0;
    const abuseCount = Number(headers[TotalContactHeaders.AbuseCount]) || 0;
    const inactiveCount = Number(headers[TotalContactHeaders.InactiveCount]) || 0;
    const staleCount = Number(headers[TotalContactHeaders.StaleCount]) || 0;
    const transactionalCount = Number(headers[TotalContactHeaders.TransactionalCount]) || 0;
    const activeCount = Number(headers[TotalContactHeaders.ActiveCount]) || 0;
    const engagedCount = Number(headers[TotalContactHeaders.EngagedCount]) || 0;
    const notconfirmedCount = Number(headers[TotalContactHeaders.NotconfirmedCount]) || 0;

    return {
        totalCount,
        bouncedCount,
        unsubscribedCount,
        abuseCount,
        inactiveCount,
        staleCount,
        transactionalCount,
        activeCount,
        engagedCount,
        notconfirmedCount,
    };
};

function* loadContacts(action: ContactsFetchStartAction): any {
    const { payload } = action;
    try {
        const currentContacts = yield select(getLoadedContacts) || [];

        const offset =
            payload.params.offset ??
            Math.ceil(currentContacts.length / DEFAULT_PER_PAGE_COUNT) * DEFAULT_PER_PAGE_COUNT;

        const resp = yield call(getContacts, { ...payload.params, offset });

        const counts = getCountsFromHeaders(resp.headers);

        const updatedContacts = resp.data;

        yield put(ContactsLoadingSuccess(updatedContacts, counts));
    } catch (e) {
        yield put(ContactsLoadingError([]));
        Logger.error(e);
        yield put(
            ShowDialog({
                title: i18n.t('error'),
                body: e?.response?.data?.Error,
                cancelLabel: i18n.t('close'),
            })
        );
    }
}

function* watchGetContacts(): any {
    yield takeLatest(ContactsActionTypes.FETCH_START, loadContacts);
}

function* getSingleContact(action: SingleContactFetchStartAction): any {
    try {
        const contact = yield call(loadSingleContact, action.payload.email);

        yield put(SingleContactLoadingSuccess(contact, action.meta));
    } catch (e) {
        yield put(SingleContactLoadingError(action.meta, e));

        if (action.payload.showErrorToast) {
            yield put(
                ShowDialog({
                    title: i18n.t('error'),
                    body: e?.response?.data?.Error,
                    cancelLabel: i18n.t('close'),
                })
            );
        }
    }
}

function* watchGetSingleContact(): any {
    yield takeLatest(ContactsActionTypes.FETCH_CONTACT_START, getSingleContact);
}

function* bulkChangeContacts(action): any {
    try {
        yield call(bulkUpdateContacts, action.payload.params);

        let fieldName = action.payload.params.field;
        if (action.payload.params.field.toLowerCase() === 'status') {
            fieldName = i18n.t('statusofcontacts');
        }
        if (action.payload.params.field.toLowerCase() === 'consenttracking') {
            fieldName = i18n.t('consenttracking');
        }
        yield put(ContactsBulkUpdateSuccess(action.meta));
        yield put(
            ShowToast({
                severity: 'info',
                message: `${fieldName} ${i18n.t('hasbeenchanged')}.`,
                duration: 6000,
                onClose: HideToast,
            })
        );
        //yield put(ContactsStartLoading())
    } catch (e) {
        yield put(ContactsBulkUpdateError(action.meta));
        Logger.error(e);
        yield put(
            ShowDialog({
                title: i18n.t('error'),
                body: e?.response?.data?.Error,
                cancelLabel: i18n.t('close'),
            })
        );
    }
}

function* watchBulkUpdateContacts(): any {
    yield takeLatest(ContactsActionTypes.BULK_UPDATE_START, bulkChangeContacts);
}

function* removeContacts(action): any {
    try {
        yield call(deleteContacts, action.payload.params.deleteParams);
        //refresh pagination
        const contacts: Contact[] = yield select(getLoadedContacts);
        const loadParams = action.payload.params?.loadParams;
        const contactTranslation =
            action.payload.params.deleteParams?.emails?.length === 1 ? i18n.t('contact') : i18n.t('contacts');
        yield put(
            ShowToast({
                severity: 'info',
                message: `${contactTranslation} ${i18n.t('deleted').toLowerCase()}.`,
                duration: 6000,
                onClose: HideToast,
            })
        );
        if (action.payload.params.deleteParams?.emails?.length > 0) {
            yield put(
                ContactsDeleteSuccess(
                    contacts.filter((o): boolean => !action.payload.params.deleteParams.emails.includes(o.Email))
                )
            );
        } else {
            contacts && (yield put(ContactsDeleteSuccess(contacts)));
        }

        loadParams && (yield put(ContactsStartLoading(loadParams)));
    } catch (e) {
        yield put(ContactsDeleteError());
        Logger.error(e);

        yield call(errorToast, e);
    }
}

function* watchRemoveContacts(): any {
    yield takeLatest(ContactsActionTypes.DELETE_START, removeContacts);
}

function* getSingleContactHistory(action): any {
    try {
        const contactHistory = yield call(getContactHistory, action.payload.params);

        if (!contactHistory || !contactHistory.data) {
            return;
        }

        const counts = Number(contactHistory.headers[TotalContactHeaders.TotalCount]) || 0;

        yield put(ContactGetHistorySuccess(contactHistory.data, counts));
        //refresh pagination
    } catch (e) {
        yield put(ContactGetHistoryError());
        Logger.error(e);
        yield put(
            ShowDialog({
                title: i18n.t('error'),
                body: e?.response?.data?.Error,
                cancelLabel: i18n.t('close'),
            })
        );
    }
}

function* watchGetSingleContactHistory(): any {
    yield takeLatest(ContactsActionTypes.GET_HISTORY_START, getSingleContactHistory);
}

function* updateSingleContact(action): any {
    try {
        const contact = yield call(editSingleContact, action.payload.params);
        yield put(ContactUpdateSuccess(contact));
        //refresh pagination
        yield put(
            ShowToast({
                severity: 'info',
                message: `${i18n.t('saved')}.`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ContactUpdateError());
        Logger.error(e);
        yield put(
            ShowDialog({
                title: i18n.t('error'),
                body: e?.response?.data?.Error,
                cancelLabel: i18n.t('close'),
            })
        );
    }
}

function* watchEditSingleContact(): any {
    yield takeLatest(ContactsActionTypes.UPDATE_START, updateSingleContact);
}

function* contactsUnsubscribedOverviewSaga(action: GetContactsUnsubscribedOverviewAction): any {
    try {
        const { from, to } = action.payload;

        const response = yield call(getContactsUnsubscribedOverview, from, to);

        const overview = response.data;

        const unsubReasonsOrder = [
            UnsubReason.Unknown,
            UnsubReason.NoLongerWant,
            UnsubReason.NeverConsented,
            UnsubReason.IrrelevantContent,
            UnsubReason.TooFrequent,
            UnsubReason.DeceptiveContent,
        ];
        const unsubReasonsArray = overview.Reasons;
        const unsubReasons = [];

        forEach(unsubReasonsOrder, (el) => {
            const reason = find(unsubReasonsArray, (o) => o.Reason === el);
            unsubReasons.push(reason);
        });

        const { unsubscribedContacts } = yield select((state): any => state.contacts);

        const pagination: ContactsUnsubscribedPaginationInterface = {
            ...unsubscribedContacts.pagination,
            ...(from && { from: dayjs(from).format(SHORT_DATE_FORMAT) }),
            ...(to && { to: dayjs(to).format(SHORT_DATE_FORMAT) }),
        };

        yield put(replace({ search: stringify(pagination as any) }));

        const updatedUnsubscribedContacts = {
            ...unsubscribedContacts,
            overview,
            pagination,
            unsubReasons,
        };

        yield put(GetContactsUnsubscribedOverviewSuccess(updatedUnsubscribedContacts));
    } catch (e) {
        yield put(GetContactsUnsubscribedOverviewError());
        yield call(errorToast, e);
    }
}

function* watchContactsUnsubscribedOverview(): any {
    yield takeLatest(ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW, contactsUnsubscribedOverviewSaga);
}

function* contactsUnsubscribedSaga(action: GetContactsUnsubscribedAction): any {
    try {
        const { from, to, limit = DEFAULT_PER_PAGE_COUNT, offset = 0, email, orderBy, statuses } = action.payload;

        const activePage = calculateActivePage(offset, limit);
        const response = yield call(getContactsUnsubscribed, { statuses, from, to, limit, offset, orderBy, email });

        const statusCount = extractUnsubscribedContactsStatusCounts(response.headers);
        const contactsList = response.data;
        const { unsubscribedContacts } = yield select((state): any => state.contacts);

        const urlQueryString: ContactsUnsubscribedPaginationInterface = {
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            to: dayjs(to).format(SHORT_DATE_FORMAT),
            ...(email ? { email } : {}),
            ...(statuses ? { statuses } : {}),
            perPage: limit,
            activePage,
            ...(orderBy ? { orderBy } : {}),
        };
        yield put(replace({ search: stringify(urlQueryString as any) }));

        const updatedUnsubscribedContacts = {
            ...unsubscribedContacts,
            contactsList,
            pagination: { ...urlQueryString },
            statusCount,
        };

        yield put(GetContactsUnsubscribedSuccess(updatedUnsubscribedContacts));
    } catch (e) {
        yield put(GetContactsUnsubscribedError());
        yield call(errorToast, e);
    }
}

function* watchContactsUnsubscribed(): any {
    yield takeLatest(ContactsActionTypes.GET_CONTACTS_UNSUBSCRIBED, contactsUnsubscribedSaga);
}

function* exportContactsUnsubscribedSaga(action: ExportContactsUnsubscribedAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    try {
        const resp = yield call(exportContactsUnsubscribed, payload);

        while (checkExportStatus) {
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }

            delay(500);
        }

        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('contactsexported'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(push(ExportsPaths.Exports));
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchExportContactsUnsubscribed(): any {
    yield takeLatest(ContactsActionTypes.EXPORT_CONTACTS_UNSUBSCRIBED, exportContactsUnsubscribedSaga);
}

function* startReshardingContacts(action: ThunkSuccessAction): any {
    try {
        yield call(reshardContacts);
        yield put(ThunkSuccess({ meta: action.meta }));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* watchForReshardingContacts(): any {
    yield takeLatest(ContactsActionTypes.RESHARD_CONTACTS_START, startReshardingContacts);
}

function* checkStatusOfContactSharding(action: CheckReshardContactsStatusAction): any {
    while (true) {
        yield delay(10_000);
        try {
            const { data: status } = yield call(checkReshardStatus);
            if ([ContactReshardStatus.Completed].includes(status)) {
                yield put(CancelReshardStatusPolling(action.meta));
            }
        } catch (e) {
            yield call(errorToast, e);

            yield put(CancelReshardStatusPolling());
        }
    }
}

function* watchPollingReshardStatus(): any {
    while (true) {
        const pollAction = yield take(ContactsActionTypes.RESHARD_CONTACTS_STATUS_START_LONG_POLLING);
        yield race([
            call(checkStatusOfContactSharding, pollAction),
            take(ContactsActionTypes.CANCEL_RESHARD_STATUS_LONG_POLLING),
        ]);
    }
}

const contactSagas = [
    fork(watchEditSingleContact),
    fork(watchGetSingleContactHistory),
    fork(watchGetContactsOverview),
    fork(watchGetContactsPattern),
    fork(watchPostContactsWithPattern),
    fork(watchPostContacts),
    fork(watchAddSingleContact),
    fork(watchAddMultipleContacts),
    fork(watchUpdatePostContactParams),
    fork(watchGetContacts),
    fork(watchRemoveContacts),
    fork(watchGetSingleContact),
    fork(watchBulkUpdateContacts),
    fork(watchGetContactGrowth),
    fork(watchContactsUnsubscribedOverview),
    fork(watchContactsUnsubscribed),
    fork(watchExportContactsUnsubscribed),
    fork(watchForReshardingContacts),
    fork(watchPollingReshardStatus),
];

export default contactSagas;
